import { Component } from 'react'
import { LocalStorage, AuthService } from '../../Services'
import { FaChevronRight } from 'react-icons/fa'
import { FiSun, FiMoon, FiLogOut, FiColumns, FiLifeBuoy, FiGlobe, FiClipboard } from 'react-icons/fi'
import logo from '../../Assets/logo.svg'
import logoNatale from '../../Assets/logo_natale.png'
import styled from 'styled-components'
import { ThemeContext, LanguageContext } from '../../theme'
import CalendarWidget from './CalendarWidget'

const NavbarContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  padding: 0 2rem;
  background: #ffffff;
  border-bottom: 1px solid #e5e5e5;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 45px;
  }
`

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

const BreadcrumbItem = styled.button`
  background: none;
  border: none;
  color: ${props => props.active ? '#000' : '#666'};
  font-size: 0.825rem;
  font-weight: ${props => props.active ? '600' : '400'};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: ${props => props.active ? 'default' : 'pointer'};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    background: ${props => props.active ? 'transparent' : '#fafafa'};
    color: ${props => props.active ? '#000' : '#000'};
  }

  &:focus {
    outline: none;
  }
`

const Separator = styled(FaChevronRight)`
  color: #999;
  font-size: 0.625rem;
  flex-shrink: 0;
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  gap: 2px;

  img {
    height: 26px;
    width: auto;
    object-fit: contain;
  }

  @media (max-width: 1024px) {
    padding: 0 0.5rem;

    img {
      height: 20px;
    }
  }
`

const LogoText = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: 0.5px;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  justify-content: flex-end;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`

const SimpleUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    display: none;
  }
`

const SimpleUserName = styled.span`
  font-size: 0.825rem;
  color: #333;
  font-weight: 500;
`

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.625rem;
  height: 32px;
  background: transparent;
  border: none;
  color: #666;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  user-select: none;

  svg {
    font-size: 1rem;
    flex-shrink: 0;
  }

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  &:active {
    background: #fee2e2;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    padding: 0 0.5rem;
    min-width: 32px;
    height: 28px;

    span {
      display: none;
    }
  }
`

const NavBaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: #666;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  &:hover {
    background: #f0f0f0;
    color: #111;
  }

  &:active {
    background: #e8e8e8;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;

    svg {
      font-size: 1rem;
    }
  }
`

const ThemeToggleButton = styled(NavBaseButton)``

const LanguageToggleButton = styled(NavBaseButton)`
  width: auto;
  gap: 4px;
  padding: 0 6px;
`

const MobileUserInfo = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: #666;
    color: white;
    font-weight: 500;
    font-size: 0.75rem;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #000;
      transform: scale(1.02);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
  }
`

const NavIconButton = styled(NavBaseButton)`
  color: ${({ $ticket }) => $ticket ? '#3b82f6' : '#666'};

  &:hover {
    background: ${({ $ticket }) => $ticket ? '#eff6ff' : '#f0f0f0'};
    color: ${({ $ticket }) => $ticket ? '#2563eb' : '#111'};
  }
`

const NavDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e0e0e0;
  flex-shrink: 0;
  ${({ $hideOnMobile }) => $hideOnMobile && `
    @media (max-width: 768px) { display: none; }
  `}
`

const LANG_LABELS = { it: 'IT', en: 'EN', fr: 'FR' }

export default class NavbarObj extends Component {
  constructor (props) {
    super(props)
    this.state = {
      navbar: false,
      text: '',
      pageName: '',
      subPageName: '',
      numberPages: 0,
      user: {}
    }
  }

  async componentDidMount () {
    this.intervalCheck = setInterval(() => this.check(), 250)
  }

  componentWillUnmount () {
    clearInterval(this.intervalCheck)
  }

  check () {
    const user = LocalStorage.get('user')
    const location = window.location.pathname
    if (location !== '/' && location !== '/login' && location !== '/firstlogin' && location !== '/firstotp' && location !== '/resetpassword') {
      const page = window.location.pathname.split('/')[1]
      const subPage = window.location.pathname.split('/')[2]
      const pageName = page !== ''
        ? page[0].toUpperCase() + page.slice(1)
        : ''
      // Hide ObjectId-like subpages from breadcrumb (e.g. /board/69bd185f...)
      const isObjectId = subPage && /^[a-f0-9]{24}$/i.test(subPage)
      const subPageName = subPage && !isObjectId ? subPage[0].toUpperCase() + subPage.slice(1) : ''
      let numberPages = 0
      if (pageName === 'Home') {
        numberPages = 0
      } else if (pageName !== 'Home' && !subPageName) {
        numberPages = 1
      } else {
        numberPages = 2
      }
      this.setState({ user, pageName, subPageName, navbar: true, numberPages })
    } else {
      this.setState({ navbar: false })
    }
  }

  getUserInitials (user) {
    if (user.role === 'azienda' && user.azienda?.intestazione) {
      return user.azienda.intestazione.substring(0, 2).toUpperCase()
    } else if (user.nome && user.cognome) {
      return `${user.nome[0]}${user.cognome[0]}`.toUpperCase()
    }
    return 'U'
  }

  getUserDisplayName (user) {
    if (user.role === 'azienda' && user.azienda?.intestazione) {
      return user.azienda.intestazione
    } else if (user.nome && user.cognome) {
      return `${user.nome} ${user.cognome}`
    }
    return 'Utente'
  }

  getSeasonalLogo () {
    const now = new Date()
    const currentYear = now.getFullYear()
    const month = now.getMonth() + 1 // getMonth() returns 0-11
    const day = now.getDate()

    // Dal 6 novembre al 31 dicembre dell'anno corrente
    const startChristmas = new Date(currentYear, 10, 6) // 10 = novembre (0-indexed)

    if (now >= startChristmas || (month === 1 && day <= 6)) {
      return logoNatale
    }

    return logo
  }

  async logout () {
    await AuthService.logout(this.props.navigate)
  }

  renderBreadcrumb () {
    const { pageName, subPageName, numberPages } = this.state

    const items = []
    items.push(
      <BreadcrumbItem
        key='home'
        active={numberPages === 0}
        onClick={() => this.props.navigate('/home')}
      >
        Home
      </BreadcrumbItem>
    )

    if (numberPages >= 1) {
      items.push(<Separator key='sep1' />)
      items.push(
        <BreadcrumbItem
          key='page'
          active={numberPages === 1}
          onClick={() => numberPages === 2 ? this.props.navigate(`/${pageName.toLowerCase()}`) : null}
        >
          {pageName}
        </BreadcrumbItem>
      )
    }

    if (numberPages === 2) {
      items.push(<Separator key='sep2' />)
      items.push(
        <BreadcrumbItem key='subpage' active>
          {subPageName}
        </BreadcrumbItem>
      )
    }

    return items
  }

  render () {
    const { navbar, user } = this.state
    if (!navbar) return null
    if (!user) return null

    const initials = this.getUserInitials(user)
    const displayName = this.getUserDisplayName(user)

    return (
      <NavbarContainer>
        <BreadcrumbContainer>
          {this.renderBreadcrumb()}
        </BreadcrumbContainer>

        <LogoContainer>
          {(() => {
            const currentLogo = this.getSeasonalLogo()
            return (
              <>
                <img src={currentLogo} alt='Logo' />
                {currentLogo === logo && <LogoText>EMS</LogoText>}
              </>
            )
          })()}
        </LogoContainer>

        <RightSection>
          <SimpleUserInfo>
            <SimpleUserName>{displayName}</SimpleUserName>
          </SimpleUserInfo>

          <NavDivider $hideOnMobile />

          <MobileUserInfo title={displayName}>
            {initials}
          </MobileUserInfo>

          <ThemeContext.Consumer>
            {({ isDark, toggleTheme }) => (
              <ThemeToggleButton
                onClick={toggleTheme}
                title={isDark ? 'Passa a tema chiaro' : 'Passa a tema scuro'}
              >
                {isDark ? <FiSun /> : <FiMoon />}
              </ThemeToggleButton>
            )}
          </ThemeContext.Consumer>

          <LanguageContext.Consumer>
            {({ language, setLanguage, supportedLanguages }) => {
              const nextLang = () => {
                const idx = supportedLanguages.indexOf(language)
                return supportedLanguages[(idx + 1) % supportedLanguages.length]
              }
              return (
                <LanguageToggleButton
                  onClick={() => setLanguage(nextLang())}
                  title={`Lingua: ${language.toUpperCase()} — clicca per cambiare`}
                  aria-label={`Lingua corrente: ${language.toUpperCase()}`}
                >
                  <FiGlobe />
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em' }}>
                    {LANG_LABELS[language] || language.toUpperCase()}
                  </span>
                </LanguageToggleButton>
              )
            }}
          </LanguageContext.Consumer>

          <NavIconButton
            onClick={() => this.props.navigate('/board')}
            title='Board'
          >
            <FiColumns />
          </NavIconButton>

          <NavIconButton $ticket onClick={() => this.props.navigate('/ticket')} title='Ticket Assistenza'>
            <FiLifeBuoy />
          </NavIconButton>

          {(this.state.user?.role === 'azienda' || this.state.user?.app?.resoconti?.enabled) && (
            <NavIconButton onClick={() => this.props.navigate('/resoconti')} title='Resoconti'>
              <FiClipboard />
            </NavIconButton>
          )}

          <CalendarWidget navigate={this.props.navigate} />

          <NavDivider />

          <LogoutButton onClick={() => this.logout()}>
            <FiLogOut />
            <span>Logout</span>
          </LogoutButton>
        </RightSection>
      </NavbarContainer>
    )
  }
}
